/**
 * Workflow definition contract for typed multi-step AI execution.
 *
 * Invariants:
 * - Workflows declare typed context, initial step, and transition graph.
 * - Permission hooks gate create/edit/view before runtime mutation.
 *
 * @see `packages/core/docs/ai/agents.md`
 */
import type { Condition, ExpressionBuilder, Transaction } from "@rocicorp/zero";
import { z } from "zod/v4";
import type { Context, Schema } from "../../zero/schema";
import type { UserActor } from "../../actor";
import type { AnyStep, InferStepInput, InferStepOutput } from "./step";
import type { ErrorInfo } from "../common";

export type { ErrorInfo };

// ============================================================================
// Context vs Input Distinction
// ============================================================================
// `context` = workflow-wide data available to ALL steps (stored on workflowExecution)
// `input` = step-specific data from transitions (stored on each workflowExecutionStep)
//
// This separation allows:
// - Workflow context (mealPlanId, userId) accessible in every handler without
//   passing through transitions
// - Step inputs are purely transition-derived data

// ============================================================================
// Transition Types
// ============================================================================

export type TransitionTarget<StepName extends string, Input> = {
  to: StepName;
  input: Input;
};

export type TransitionResult<Steps extends Record<string, AnyStep>> =
  | TransitionTarget<keyof Steps & string, InferStepInput<Steps[keyof Steps & string]>>
  | TransitionTarget<keyof Steps & string, InferStepInput<Steps[keyof Steps & string]>>[]
  | null;

type TransitionFn<
  Steps extends Record<string, AnyStep>,
  FromStepName extends keyof Steps & string,
> = (output: InferStepOutput<Steps[FromStepName]>) => TransitionResult<Steps>;

type TypedTransitionTarget<
  Steps extends Record<string, AnyStep>,
  ToStepName extends keyof Steps & string,
> = {
  to: ToStepName;
  input: InferStepInput<Steps[ToStepName]>;
};

type TypedTransitionResult<
  Steps extends Record<string, AnyStep>,
  ToStepNames extends keyof Steps & string = keyof Steps & string,
> = TypedTransitionTarget<Steps, ToStepNames> | TypedTransitionTarget<Steps, ToStepNames>[] | null;

type TypedTransitionFn<
  Steps extends Record<string, AnyStep>,
  FromStepName extends keyof Steps & string,
  ToStepNames extends keyof Steps & string = keyof Steps & string,
> = (output: InferStepOutput<Steps[FromStepName]>) => TypedTransitionResult<Steps, ToStepNames>;

export type AwaitFanInDeclaration<
  Steps extends Record<string, AnyStep>,
  TargetStepName extends keyof Steps & string,
  CollectStepName extends keyof Steps & string = keyof Steps & string,
> = {
  awaitFanIn: {
    from: keyof Steps & string;
    collect: CollectStepName;
  };
  input: (
    outputs: InferStepOutput<Steps[CollectStepName]>[],
  ) => InferStepInput<Steps[TargetStepName]>;
};

type AwaitFanInDeclarationUnion<
  Steps extends Record<string, AnyStep>,
  TargetStepName extends keyof Steps & string,
> = keyof Steps extends infer CollectStep
  ? CollectStep extends keyof Steps & string
    ? AwaitFanInDeclaration<Steps, TargetStepName, CollectStep>
    : never
  : never;

export type TransitionDefinition<
  Steps extends Record<string, AnyStep>,
  StepName extends keyof Steps & string,
> = TypedTransitionFn<Steps, StepName> | AwaitFanInDeclarationUnion<Steps, StepName> | null;

export type Transitions<Steps extends Record<string, AnyStep>> = {
  [K in keyof Steps]?: TransitionDefinition<Steps, K & string>;
};

// ============================================================================
// Workflow Permissions
// ============================================================================

export type WorkflowCanCreate<Context_> = (opts: {
  tx: Transaction;
  ctx: Context;
  args: { context: Context_ };
}) => Promise<boolean>;

export type WorkflowCanEdit = (opts: {
  tx: Transaction;
  ctx: Context;
  args: { executionId: string };
}) => Promise<boolean>;

export type WorkflowViewable = (
  eb: ExpressionBuilder<"workflowExecution", Schema>,
  actor: UserActor,
) => Condition;

// ============================================================================
// Lifecycle Hook Types
// ============================================================================

export type OnCompletePreCommitArgs<Context_> = {
  tx: Transaction;
  execution: {
    id: string;
    workflowType: string;
    workflowVersion: number;
    context: Context_;
  };
};

export type OnCompletePreCommitCallback<Context_ = unknown, PreCommitResult = void> = (
  args: OnCompletePreCommitArgs<Context_>,
) => Promise<PreCommitResult>;

export type OnErrorPreCommitArgs<Context_> = {
  tx: Transaction;
  execution: {
    id: string;
    workflowType: string;
    workflowVersion: number;
    context: Context_;
  };
  error: ErrorInfo;
};

export type OnErrorPreCommitCallback<Context_ = unknown> = (
  args: OnErrorPreCommitArgs<Context_>,
) => Promise<void>;

export type WorkflowHooks<Context_ = unknown, PreCommitResult = void> = {
  onCompletePreCommit?: OnCompletePreCommitCallback<Context_, PreCommitResult>;
  onErrorPreCommit?: OnErrorPreCommitCallback<Context_>;
};

// ============================================================================
// Workflow Definition
// ============================================================================

export type Workflow<
  WorkflowType extends string = string,
  Version extends number = number,
  Context_ = unknown,
  Steps extends Record<string, AnyStep> = Record<string, AnyStep>,
  PreCommitResult = void,
> = {
  workflowType: WorkflowType;
  version: Version;
  contextSchema: z.ZodType<Context_>;
  steps: Steps;
  initialStep: keyof Steps & string;
  transitions: Transitions<Steps>;
  canCreate: WorkflowCanCreate<Context_>;
  canEdit: WorkflowCanEdit;
  viewable: WorkflowViewable;
  hooks: WorkflowHooks<Context_, PreCommitResult>;

  onComplete: <PCR = void>(
    callback: OnCompletePreCommitCallback<Context_, PCR>,
  ) => Workflow<WorkflowType, Version, Context_, Steps, PCR>;

  onError: (
    callback: OnErrorPreCommitCallback<Context_>,
  ) => Workflow<WorkflowType, Version, Context_, Steps, PreCommitResult>;
};

export type AnyWorkflow = Workflow<string, number, any, any, any>;

export type InferWorkflowContext<W extends AnyWorkflow> = z.infer<W["contextSchema"]>;
export type InferWorkflowPreCommitResult<W extends AnyWorkflow> =
  W extends Workflow<any, any, any, any, infer PCR> ? PCR : void;

type DefineWorkflowArgs<
  WorkflowType extends string,
  Version extends number,
  Context_,
  Steps extends Record<string, AnyStep>,
> = {
  workflowType: WorkflowType;
  version: Version;
  contextSchema: z.ZodType<Context_>;
  steps: Steps;
  initialStep: keyof Steps & string;
  transitions: Transitions<Steps>;
  canCreate: WorkflowCanCreate<Context_>;
  canEdit: WorkflowCanEdit;
  viewable: WorkflowViewable;
};

export function defineWorkflow<
  WorkflowType extends string,
  Version extends number,
  Context_,
  Steps extends Record<string, AnyStep>,
>(
  args: DefineWorkflowArgs<WorkflowType, Version, Context_, Steps>,
): Workflow<WorkflowType, Version, Context_, Steps, void> {
  function createWorkflow<PCR = void>(
    hooks: WorkflowHooks<Context_, PCR>,
  ): Workflow<WorkflowType, Version, Context_, Steps, PCR> {
    const workflow: Workflow<WorkflowType, Version, Context_, Steps, PCR> = {
      workflowType: args.workflowType,
      version: args.version,
      contextSchema: args.contextSchema,
      steps: args.steps,
      initialStep: args.initialStep,
      transitions: args.transitions,
      canCreate: args.canCreate,
      canEdit: args.canEdit,
      viewable: args.viewable,
      hooks,

      onComplete: <NewPCR = void>(callback: OnCompletePreCommitCallback<Context_, NewPCR>) => {
        return createWorkflow<NewPCR>({
          ...hooks,
          onCompletePreCommit: callback,
        } as WorkflowHooks<Context_, NewPCR>);
      },

      onError: (callback: OnErrorPreCommitCallback<Context_>) => {
        return createWorkflow<PCR>({
          ...hooks,
          onErrorPreCommit: callback,
        });
      },
    };

    return workflow;
  }

  return createWorkflow<void>({});
}

// ============================================================================
// Type Guards
// ============================================================================

export function isTransitionFunction<Steps extends Record<string, AnyStep>>(
  transition: TransitionDefinition<Steps, keyof Steps & string> | undefined,
): transition is TransitionFn<Steps, keyof Steps & string> {
  return typeof transition === "function";
}

export function isFanInTransition<Steps extends Record<string, AnyStep>>(
  transition: TransitionDefinition<Steps, keyof Steps & string> | undefined,
): transition is AwaitFanInDeclarationUnion<Steps, keyof Steps & string> {
  return transition !== null && typeof transition === "object" && "awaitFanIn" in transition;
}
